// Builders: persistenza DB per profilo, piani, errori di generazione, e
// strutturazione meal plan in tabelle dedicate (per shopping list deterministica).
// Helper: matchIngredient (fuzzy ingredient lookup), parseQty (parser quantità libera).
//
// PR-3b (ADR-0005). Estratto a parità funzionale da coach/index.html.
//
// Dipendenze esterne (lette da window.*, esposte dallo <script> classic inline):
//   window.sb            → Supabase client (CDN-based v2, flowType:'implicit')
//   window.currentUser   → { id, email, ... } sessione utente attiva
//   window.S             → state in-memory ({ profile, plans, ingredientsDict, ... })
//   window._welcomeFlags → snapshot onboarding_flags (merge welcome/consent/legal)

import { dbSlot } from './core.js';

export async function saveProfile(profile) {
  // Migra eventuali flag pre-onboarding (welcome / email_consent / legal) da
  // localStorage al JSONB onboarding_flags, in un solo upsert. Cleanup chiavi
  // locali a buon fine. Idempotente: se le chiavi non ci sono (utenti già
  // migrati o esistenti), no-op.
  const merged = { ...(profile.onboarding_flags || window._welcomeFlags || {}) };
  const wKey = `coachai_welcome_v1_${window.currentUser.id}`;
  const cKey = `coachai_email_consent_${window.currentUser.id}`;
  const lKey = 'coachai_legal_v1_pending'; // chiave globale: scritta al signup, prima che esista currentUser.id
  let hadWelcome = false, hadConsent = false, hadLegal = false;
  if (localStorage.getItem(wKey) === '1') {
    merged.welcome_v1 = true;
    hadWelcome = true;
  }
  const cRaw = localStorage.getItem(cKey);
  if (cRaw !== null) {
    try {
      const c = JSON.parse(cRaw);
      merged.email_consent = !!c.accepted;
      merged.email_consent_at = c.at || new Date().toISOString();
      hadConsent = true;
    } catch (_) { /* JSON corrotto: ignora, niente migrazione */ }
  }
  const lRaw = localStorage.getItem(lKey);
  if (lRaw !== null) {
    try {
      const l = JSON.parse(lRaw);
      if (l.accepted === true) {
        merged.legal_acceptance_v1 = true;
        merged.legal_acceptance_at = l.at || new Date().toISOString();
        hadLegal = true;
      }
    } catch (_) { /* JSON corrotto: ignora, niente migrazione */ }
  }
  const payload = Object.keys(merged).length > 0
    ? { ...profile, onboarding_flags: merged }
    : profile;
  await window.sb.from('profiles').upsert({ id: window.currentUser.id, ...payload, updated_at: new Date().toISOString() });
  // Cleanup post-success: rimuovi solo le chiavi effettivamente migrate
  if (hadWelcome) try { localStorage.removeItem(wKey); } catch (_) {}
  if (hadConsent) try { localStorage.removeItem(cKey); } catch (_) {}
  if (hadLegal) try { localStorage.removeItem(lKey); } catch (_) {}
  // Sync in-memory: il prossimo render userà i flag aggiornati
  window._welcomeFlags = merged;
  if (window.S.profile) window.S.profile.onboarding_flags = merged;
}

export async function savePlan(tipo, data) {
  const generato_con = { profilo: window.S.profile, ultima_misurazione: window.S.lastMeasurement || null };
  const { data: inserted, error } = await window.sb.from('plans')
    .insert({ user_id: window.currentUser.id, tipo, data, generato_con })
    .select('id')
    .single();
  if (error) { console.error('savePlan', error); throw error; }
  return inserted?.id || null;
}

// Log silenzioso degli errori di generazione su public.gen_errors per analisi
// offline (admin via dashboard). L'utente vede solo un messaggio amichevole;
// noi otteniamo i dati per cercare pattern (shape JSON invalida vs HTTP fail
// vs timeout Haiku) senza dover chiedere screenshot. Privacy: niente prompt,
// niente note_alimentari nel context. Fail silently per non cascadeare errori
// in UI (un log fallito non deve mai rompere il flusso utente).
export async function logGenError(tipo, err, context) {
  if (!window.currentUser?.id) return;
  try {
    await window.sb.from('gen_errors').insert({
      user_id: window.currentUser.id,
      tipo: String(tipo).slice(0, 64),
      error_message: (err?.message || '').slice(0, 500) || null,
      error_name: (err?.name || '').slice(0, 100) || null,
      context: context || {}
    });
  } catch (logErr) {
    console.error('logGenError silent fail', logErr);
  }
}

// Dictionary ingredienti: caricato lazy al primo uso, cached in S.
// Struttura: Map<alias_lowercase, {id, canonical_name, category, default_unit, pack_size}>.
export async function loadIngredientsDict() {
  if (window.S.ingredientsDict) return window.S.ingredientsDict;
  const { data, error } = await window.sb.from('ingredients')
    .select('id, canonical_name, category, default_unit, aliases, pack_size');
  if (error) { console.error('loadIngredientsDict', error); return new Map(); }
  const m = new Map();
  (data || []).forEach(row => {
    const entry = { id: row.id, canonical_name: row.canonical_name,
                    category: row.category, default_unit: row.default_unit,
                    pack_size: row.pack_size };
    m.set(row.canonical_name.toLowerCase(), entry);
    (row.aliases || []).forEach(a => m.set(String(a).toLowerCase(), entry));
  });
  window.S.ingredientsDict = m;
  return m;
}

// Match nome raw → ingredient_id. Prima esatto, poi parziale (contains).
// Ritorna null se nessun match soddisfacente (ingredient_id resterà NULL nel DB).
export function matchIngredient(rawName, dict) {
  if (!rawName) return null;
  const key = String(rawName).trim().toLowerCase();
  if (dict.has(key)) return dict.get(key);
  // Match parziale: cerca se il raw contiene o è contenuto in una chiave nota
  for (const [k, v] of dict) {
    if (key.includes(k) && k.length >= 4) return v;  // soglia 4 char per evitare false positive (es. 'il','di')
  }
  return null;
}

// Parser quantità libera "150g", "1.5 kg", "2 cucchiai", "1 pz" → {quantity, unit}
export function parseQty(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase().replace(',', '.');
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(kg|g|ml|l|pz|pezzi|pezzo|cucchiaio|cucchiaini|cucchiaino|cucchiai|fette|fetta)?/);
  if (!m) return null;
  const q = parseFloat(m[1]);
  let u = (m[2] || 'g').replace(/[si]$/, '');  // plurali → singolari
  const unitMap = { pezzo: 'pz', pezz: 'pz', cucchiaio: 'cucchiaio',
                    cucchiain: 'cucchiaino', fetta: 'pz' };
  u = unitMap[u] || u;
  if (!['g','kg','ml','l','pz','cucchiaio','cucchiaino'].includes(u)) u = 'g';
  return { quantity: q, unit: u };
}

// Converte i 7 giorni del meal plan in rows per le tabelle strutturate.
// Match fuzzy ingredienti sul dictionary. Insert batch in 2 fasi:
//   1) meal_plan_items (parent)
//   2) meal_plan_ingredients (children, con FK agli item appena inseriti)
export async function persistMealPlanStructured(planId, days) {
  if (!planId || !Array.isArray(days)) return;
  const dict = await loadIngredientsDict();
  // Fase 1: build rows items + memorizza mapping (day,slot,itemIdx) → ingredienti
  const itemRows = [];
  const itemIngredientsPlan = [];  // [{ itemKey, ingredients: [...] }]
  days.forEach((day, di) => {
    const slotCounter = { col: 0, sna: 0, pra: 0, cen: 0 };
    (day.m || []).forEach(meal => {
      const rawSlot = meal.t;
      const slot = dbSlot(rawSlot, slotCounter.sna || 0);
      slotCounter[rawSlot] = (slotCounter[rawSlot] || 0) + 1;
      (meal.items || []).forEach((it, itemIdx) => {
        const mc = it.mc || {};
        const pNum = parseFloat(String(mc.p || '').replace(/[^\d.]/g,'')) || null;
        const cNum = parseFloat(String(mc.c || '').replace(/[^\d.]/g,'')) || null;
        const fNum = parseFloat(String(mc.f || '').replace(/[^\d.]/g,'')) || null;
        const itemKey = di + '-' + slot + '-' + itemIdx;
        itemRows.push({
          plan_id: planId,
          user_id: window.currentUser.id,
          day_index: di,
          slot: slot,
          item_index: itemIdx,
          dish_name: it.nm || 'Piatto',
          dish_subtitle: it.sb || null,
          kcal: mc.k ? parseInt(mc.k) : (meal.k ? parseInt(meal.k) : null),
          proteine_g: pNum,
          carbo_g: cNum,
          grassi_g: fNum,
          steps: Array.isArray(it.st) ? it.st : []
        });
        // Estrai ingredienti: supporta sia formato nuovo {n,q,u} sia legacy [q,n]
        const ingredients = [];
        (it.ig || []).forEach((ig, order) => {
          let raw_name, quantity, unit;
          if (Array.isArray(ig) && ig.length >= 2) {
            const parsed = parseQty(ig[0]);
            if (!parsed) return;
            raw_name = String(ig[1]).trim();
            quantity = parsed.quantity;
            unit = parsed.unit;
          } else if (ig && typeof ig === 'object' && ig.n) {
            raw_name = String(ig.n).trim();
            quantity = typeof ig.q === 'number' ? ig.q : parseFloat(ig.q);
            unit = ig.u || 'g';
            if (isNaN(quantity) || quantity <= 0) return;
            if (!['g','kg','ml','l','pz','cucchiaio','cucchiaino'].includes(unit)) unit = 'g';
          } else return;
          const match = matchIngredient(raw_name, dict);
          ingredients.push({
            raw_name,
            ingredient_id: match ? match.id : null,
            quantity,
            unit,
            order_index: order
          });
        });
        itemIngredientsPlan.push({ itemKey, ingredients });
      });
    });
  });

  // Auto-growth dizionario: ogni raw_name che non ha matchato viene inserito
  // una sola volta (una riga per canonical_name univoco) con source='auto' +
  // category='dispensa' come fallback. Così shopping_list_view aggrega
  // comunque e il dizionario cresce organicamente man mano che gli utenti
  // generano piani. Curazione (fix category, aggiunta alias) è un'operazione
  // admin successiva su WHERE source='auto'.
  const toAdd = new Map();  // canonical_name → unit
  itemIngredientsPlan.forEach(p => {
    p.ingredients.forEach(ing => {
      if (ing.ingredient_id != null) return;
      const canonical = String(ing.raw_name || '').trim().toLowerCase();
      if (!canonical) return;
      if (dict.has(canonical)) return;  // già aggiunto in un giro precedente
      if (!toAdd.has(canonical)) toAdd.set(canonical, ing.unit || 'g');
    });
  });
  for (const [canonical, unit] of toAdd) {
    const validUnit = ['g','kg','ml','l','pz','cucchiaio','cucchiaino'].includes(unit) ? unit : 'g';
    try {
      const { data, error } = await window.sb.from('ingredients')
        .insert({ canonical_name: canonical, category: 'dispensa',
                  default_unit: validUnit, source: 'auto' })
        .select('id, canonical_name, category, default_unit, pack_size')
        .single();
      if (error) {
        if (error.code === '23505') {
          // race o già esiste: fetch e popola cache
          const { data: existing } = await window.sb.from('ingredients')
            .select('id, canonical_name, category, default_unit, pack_size, aliases')
            .eq('canonical_name', canonical).single();
          if (existing) {
            const entry = { id: existing.id, canonical_name: existing.canonical_name,
                            category: existing.category, default_unit: existing.default_unit,
                            pack_size: existing.pack_size };
            dict.set(canonical, entry);
            (existing.aliases || []).forEach(a => dict.set(String(a).toLowerCase(), entry));
          }
        } else {
          console.warn('[auto-growth] ingredients insert fallito per', canonical, error);
        }
        continue;
      }
      const entry = { id: data.id, canonical_name: data.canonical_name,
                      category: data.category, default_unit: data.default_unit,
                      pack_size: data.pack_size };
      dict.set(canonical, entry);
    } catch (e) {
      console.warn('[auto-growth] errore inatteso per', canonical, e);
    }
  }
  // Ri-match gli ingredienti prima non matchati: con il dict aggiornato,
  // dovrebbero ora tutti avere un ingredient_id (salvo errore insert).
  itemIngredientsPlan.forEach(p => {
    p.ingredients.forEach(ing => {
      if (ing.ingredient_id != null) return;
      const m = matchIngredient(ing.raw_name, dict);
      if (m) ing.ingredient_id = m.id;
    });
  });

  if (itemRows.length === 0) return;
  // Insert batch items, ritorna id generati
  const { data: insertedItems, error: itemsErr } = await window.sb
    .from('meal_plan_items')
    .insert(itemRows)
    .select('id, day_index, slot, item_index');
  if (itemsErr) throw itemsErr;
  // Mappa (day,slot,itemIdx) → id
  const itemIdByKey = new Map();
  (insertedItems || []).forEach(r => {
    itemIdByKey.set(r.day_index + '-' + r.slot + '-' + r.item_index, r.id);
  });
  // Insert batch ingredients
  const ingredientRows = [];
  itemIngredientsPlan.forEach(plan => {
    const itemId = itemIdByKey.get(plan.itemKey);
    if (!itemId) return;
    plan.ingredients.forEach(ing => {
      ingredientRows.push({
        meal_plan_item_id: itemId,
        ingredient_id: ing.ingredient_id,
        raw_name: ing.raw_name,
        quantity: ing.quantity,
        unit: ing.unit,
        order_index: ing.order_index
      });
    });
  });
  if (ingredientRows.length > 0) {
    const { error: ingErr } = await window.sb.from('meal_plan_ingredients').insert(ingredientRows);
    if (ingErr) throw ingErr;
  }
  console.log('persistMealPlanStructured:', itemRows.length, 'piatti +', ingredientRows.length, 'ingredienti in DB');
}
